import React, { useState } from "react";
import {
  Box,
  Button,
  TextField,
  Typography,
  Paper,
  Link,
  Alert,
  IconButton,
  InputAdornment,
  styled,
} from "@mui/material";
import { Visibility, VisibilityOff } from "@mui/icons-material";
import { useNavigate, useLocation } from "react-router-dom";
import { useConfirmForgotPasswordMutation } from "Services/auth";

const MainContainer = styled(Box)({
  display: "flex",
  minHeight: "100vh",
  backgroundColor: "#f5f5f5",
  alignItems: "center",
  justifyContent: "center",
});

const Card = styled(Paper)({
  padding: "48px",
  width: "100%",
  maxWidth: "440px",
  boxShadow: "0 4px 24px rgba(0,0,0,0.08)",
  borderRadius: "12px",
});

const SubmitButton = styled(Button)({
  background: "linear-gradient(90deg, #2A7BFF 0%, #3F8CFF 100%)",
  color: "white",
  padding: "12px",
  borderRadius: "8px",
  textTransform: "none",
  fontWeight: 600,
  fontSize: "15px",
  "&:hover": {
    background: "linear-gradient(90deg, #1D63E8 0%, #2A7BFF 100%)",
  },
});

const StyledTextField = styled(TextField)({
  marginBottom: "20px",
  "& .MuiOutlinedInput-root": { borderRadius: "8px" },
});

const ResetPassword: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [confirmForgotPassword, { isLoading }] = useConfirmForgotPasswordMutation();

  const [email, setEmail] = useState((location.state as any)?.email || "");
  const [confirmationCode, setConfirmationCode] = useState("");
  const [newPassword, setNewPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [errorMsg, setErrorMsg] = useState<string | null>(null);
  const [success, setSuccess] = useState(false);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setErrorMsg(null);
    try {
      await confirmForgotPassword({ email, confirmationCode, newPassword }).unwrap();
      setSuccess(true);
      setTimeout(() => navigate("/login"), 1500);
    } catch (err: any) {
      setErrorMsg(err?.data?.message || "Failed to reset password");
    }
  };

  return (
    <MainContainer>
      <Card>
        <Typography variant="h5" fontWeight={700} gutterBottom>
          Reset Password
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
          Enter the code sent to your email and choose a new password.
        </Typography>

        {errorMsg && <Alert severity="error" sx={{ mb: 2 }}>{errorMsg}</Alert>}
        {success && <Alert severity="success" sx={{ mb: 2 }}>Password reset! Redirecting to sign in…</Alert>}

        <form onSubmit={handleSubmit}>
          <StyledTextField
            fullWidth
            placeholder="Email"
            type="email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            required
          />
          <StyledTextField
            fullWidth
            placeholder="Reset Code"
            value={confirmationCode}
            onChange={(e) => setConfirmationCode(e.target.value)}
            required
          />
          <StyledTextField
            fullWidth
            placeholder="New Password"
            type={showPassword ? "text" : "password"}
            value={newPassword}
            onChange={(e) => setNewPassword(e.target.value)}
            required
            InputProps={{
              endAdornment: (
                <InputAdornment position="end">
                  <IconButton onClick={() => setShowPassword(!showPassword)} edge="end">
                    {showPassword ? <VisibilityOff /> : <Visibility />}
                  </IconButton>
                </InputAdornment>
              ),
            }}
          />
          <SubmitButton fullWidth type="submit" disabled={isLoading || success}>
            {isLoading ? "Resetting…" : "Reset Password"}
          </SubmitButton>
        </form>

        <Box textAlign="center" mt={3}>
          <Link component="button" variant="body2" underline="hover" onClick={() => navigate("/login")}>
            Back to Sign In
          </Link>
        </Box>
      </Card>
    </MainContainer>
  );
};

export default ResetPassword;
